import { useEffect, useRef, useState } from 'react';
import useCustomPagination from './useCustomPagination';
import { mergeNewDataIntoCurrent } from '../helpers/mergeNewDataIntoCurrent';

export const useFetchOptionsForPaginatedSelectWithSWR = ({
    client,
    route,
    searchParams = {},
    formatResponseToOptions,
}) => {
    const [options, setOptions] = useState([]);
    const [totalOptions, setTotalOptions] = useState(0);

    const [page, setPage] = useState(1);
    const [canLoadMoreOptions, setCanLoadMoreOptions] = useState(false);

    const isFirstRender = useRef(true);
    const [requestSearchParams, setRequestSearchParams] = useState(searchParams); // is used to avoid request with wrong offset when prop searchParams is changing
    const stringifiedSearchParams = JSON.stringify(searchParams);

    const [isFirstRequestCompleted, setIsFirstRequestCompleted] = useState(false); // indicates if the first data is already received

    const {
        data: results,
        loading,
        hasNextPage,
        total,
    } = useCustomPagination({
        pageIndex: page - 1,
        searchParams: requestSearchParams,
        client,
        route,
    });

    useEffect(() => {
        setOptions((prevResults) => {
            if (!prevResults && !results) {
                return [];
            } else {
                const newData = formatResponseToOptions(results);
                return mergeNewDataIntoCurrent(prevResults || [], newData || []);
            }
        });

        if (page === 1 && results) {
            setIsFirstRequestCompleted(true);
        }

        if (hasNextPage !== undefined) {
            setCanLoadMoreOptions(hasNextPage);
        }
    }, [results]);

    useEffect(() => {
        if (total && !totalOptions) {
            setTotalOptions(total);
        }
    }, [total]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // if searchParams has changed reset current data and make request with new search params
        resetCurrentData();
        setRequestSearchParams(searchParams || {});
    }, [stringifiedSearchParams]);

    const resetCurrentData = () => {
        setPage(1);
        setOptions([]);
        setTotalOptions(0);
        setIsFirstRequestCompleted(false);
    };

    return {
        options,
        optionsLoading: loading,
        canLoadMoreOptions,
        setPage,
        totalOptions,
        isFirstRequestCompleted,
    };
};
